import { CheckIcon, CloseIcon, LessonIcon } from '@sketchnote/icons';
import classNames from 'classnames';
import React, { useState } from 'react';
import { CSSTransition } from 'react-transition-group';

import styles from './snackbar.module.css';
import { OpenSnackbarParams, Positions, SnackbarProps, SnackType } from './snackbar.types';
import {
  defaultDuration,
  defaultInterval,
  defaultPosition,
  defaultSnackType,
  SnackbarContext,
} from './snackbar.utils';

export const StandardSnack = ({
  text,
  icon,
}: {
  text: React.ReactNode | JSX.Element;
  icon?: JSX.Element;
}) => (
  <div className="flex shadow-sm px-16 py-18 border border-gray-100 rounded-lg bg-white items-center">
    {icon ? (
      <span className="mr-10 h-20 w-20">{icon}</span>
    ) : (
      <span className="rounded-full h-20 w-20 bg-green-999 text-white text-xss flex items-center justify-center mr-10">
        <CheckIcon />
      </span>
    )}
    <span className="font-medium text-sm">{text}</span>
  </div>
);

export const ErrorSnack = ({
  text,
  icon,
}: {
  text: React.ReactNode | JSX.Element;
  icon?: JSX.Element;
}) => (
  <div className="flex shadow-sm px-16 py-18 border border-gray-100 rounded-lg bg-white">
    {icon ? (
      <span className="mr-10 h-20 w-20">{icon}</span>
    ) : (
      <span className="rounded-full h-20 w-20 bg-red-999 text-white text-xss flex items-center justify-center mr-10">
        <CloseIcon />
      </span>
    )}
    <span className="font-medium text-sm">{text}</span>
  </div>
);

export const CreateSnack = ({ text }: { text: string }) => (
  <div className="flex rounded-full bg-gray-999 px-16 py-14 font-medium text-white items-center justify-center">
    <LessonIcon className="mr-10" />
    <span>{text}</span>
  </div>
);

export default function Snackbar({ children }: SnackbarProps) {
  // Current open state
  const [open, setOpen] = useState<boolean>(false);

  // TODO: Replace any with correct type
  // Current timeout ID
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | any>();
  // Snackbar's text
  const [textState, setText] = useState<string>('');
  // Snackbar's duration
  const [durationState, setDuration] = useState<number>(defaultDuration);
  // Snackbar's position
  const [positionState, setPosition] = useState<Positions>(defaultPosition);
  // Snackbar's type
  const [snackTypeState, setSnackType] = useState<SnackType>('standard');
  // SnackBar Optional Icon
  const [snackIcon, setSnackIcon] = useState<JSX.Element | undefined>();

  const triggerSnackbar = ({
    text,
    duration = defaultDuration,
    position = defaultPosition,
    snackType = defaultSnackType,
    snackIcon,
  }: OpenSnackbarParams) => {
    setText(text);
    setDuration(duration);
    setPosition(position);
    setOpen(true);
    setSnackType(snackType);
    setSnackIcon(snackIcon);
  };

  // Manages all the snackbar's opening process
  const openSnackbar = ({
    text,
    duration,
    position,
    snackType = defaultSnackType,
    snackIcon,
  }: OpenSnackbarParams) => {
    // Closes the snackbar if it is already open
    if (open === true) {
      setOpen(false);
      setTimeout(() => {
        triggerSnackbar({ text, duration, position, snackType, snackIcon });
      }, defaultInterval);
    } else {
      triggerSnackbar({ text, duration, position, snackType, snackIcon });
    }
  };

  // Closes the snackbar just by setting the "open" state to false
  const closeSnackbar = () => {
    setOpen(false);
  };

  const handleOnEnter = () => {
    clearTimeout(timeoutId);
    setTimeoutId(setTimeout(() => setOpen(false), durationState));
  };

  const snackTypeSelector = (type: SnackType, icon?: JSX.Element) => {
    switch (type) {
      case 'create':
        return <CreateSnack text={textState} />;
      case 'standard':
        return <StandardSnack text={textState} icon={icon} />;
      case 'error':
        return <ErrorSnack text={textState} />;
      default:
        return <StandardSnack text={textState} />;
    }
  };

  // Returns the Provider that must wrap the application
  return (
    <SnackbarContext.Provider value={{ openSnackbar, closeSnackbar }}>
      {children}

      {/* Renders Snackbar on the end of the page */}
      <CSSTransition
        in={open}
        timeout={150}
        // Sets timeout to close the snackbar
        mountOnEnter={true}
        unmountOnExit={true}
        onEnter={handleOnEnter}
        // Sets custom classNames based on "position"
        // className={`snackbar-wrapper snackbar-wrapper-${positionState}`}
        className={classNames({
          [styles['snackbar-wrapper']]: true,
          [styles[`snackbar-wrapper-${positionState}`]]: true,
        })}
        classNames={{
          enter: `${styles['snackbar-enter']} ${styles[`snackbar-enter-${positionState}`]}`,
          enterActive: `${styles['snackbar-enter-active']} ${
            styles[`snackbar-enter-active-${positionState}`]
          }`,
          exitActive: `${styles['snackbar-exit-active']} ${
            styles[`snackbar-exit-active-${positionState}`]
          }`,
        }}
      >
        <div>{snackTypeSelector(snackTypeState, snackIcon)}</div>
      </CSSTransition>
    </SnackbarContext.Provider>
  );
}
